import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Avatar,
    Grid,
    Box,
    Divider,
    Skeleton,
    Alert,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { getLatestInvoiceData, getBillingMetadata, upsertBillingMetadata } from '../../../api_calls/api_calls_invoices';

const PROVIDER_LOGO_PATH = '/images/providers/';

const ViewPlanDetailsModal = ({ open, onClose, houseData }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [invoiceData, setInvoiceData] = useState(null);
    const [billingMetadata, setBillingMetadata] = useState(null);
    const [invoiceError, setInvoiceError] = useState(null);
    const [metadataError, setMetadataError] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            // Reset state when modal closes
            setInvoiceData(null);
            setBillingMetadata(null);
            setInvoiceError(null);
            setMetadataError(null);
            setEditingField(null);
            setEditValue('');
            setSaving(false);
            setLoading(true);
            return;
        }

        if (open && houseData && houseData.invoiceprovidername && houseData.invoicename && houseData.id) {
            setLoading(true);
            setInvoiceError(null);
            setMetadataError(null);

            let completedRequests = 0;
            const totalRequests = 2;
            const checkLoading = () => {
                completedRequests++;
                if (completedRequests === totalRequests) {
                    setLoading(false);
                }
            };

            // Fetch invoice data
            getLatestInvoiceData(houseData.invoiceprovidername, houseData.invoicename)
                .then((data) => {
                    setInvoiceData(data);
                    setInvoiceError(null);
                    checkLoading();
                })
                .catch((error) => {
                    setInvoiceError(error.message || 'Failed to load invoice data');
                    checkLoading();
                });

            // Fetch billing metadata
            getBillingMetadata(houseData.id)
                .then((data) => {
                    setBillingMetadata(data);
                    setMetadataError(null);
                    checkLoading();
                })
                .catch((error) => {
                    setMetadataError(error.message || 'Failed to load billing metadata');
                    checkLoading();
                });
        } else if (open && (!houseData || !houseData.id)) {
            setLoading(false);
        }
    }, [open, houseData]);

    const handleEditField = (fieldName, currentValue) => {
        setEditingField(fieldName);

        setEditValue(currentValue !== null && currentValue !== undefined ? String(currentValue) : '');
    };

    const handleNumericChange = (e) => {
        const value = e.target.value;
        // Allow empty string, or non-negative numbers
        if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
            setEditValue(value);
        }
    };

    const handleCancelEdit = () => {
        setEditingField(null);
        setEditValue('');
    };

    const handleSaveField = async () => {
        if (!billingMetadata || !houseData?.id) return;

        setSaving(true);
        try {
            // Prepare all fields for the upsert
            const updatedMetadata = {
                dteuroanatm: editingField === 'dteuroanatm' ? parseFloat(editValue) || 0 : (billingMetadata.dteuroanatm !== undefined && billingMetadata.dteuroanatm !== null ? billingMetadata.dteuroanatm : 0),
                dfeuroanatm: editingField === 'dfeuroanatm' ? parseFloat(editValue) || 0 : (billingMetadata.dfeuroanatm !== undefined && billingMetadata.dfeuroanatm !== null ? billingMetadata.dfeuroanatm : 0),
                timizonis: editingField === 'timizonis' ? parseFloat(editValue) || 0 : (billingMetadata.timizonis !== undefined && billingMetadata.timizonis !== null ? billingMetadata.timizonis : 0),
                palaiotita: editingField === 'palaiotita' ? (parseFloat(editValue) || 0) : (billingMetadata.palaiotita !== undefined && billingMetadata.palaiotita !== null ? billingMetadata.palaiotita : 0),
                synttap: editingField === 'synttap' ? parseFloat(editValue) || 0 : (billingMetadata.synttap !== undefined && billingMetadata.synttap !== null ? billingMetadata.synttap : 0),
                nightmeter: editingField === 'nightmeter' ? (editValue === 'true' || editValue === true) : (billingMetadata.nightmeter !== undefined && billingMetadata.nightmeter !== null ? billingMetadata.nightmeter : false),
                simfisxyspar: editingField === 'simfisxyspar' ? parseFloat(editValue) || 0 : (billingMetadata.simfisxyspar !== undefined && billingMetadata.simfisxyspar !== null ? billingMetadata.simfisxyspar : 0)
            };

            await upsertBillingMetadata(houseData.id, updatedMetadata);

            // Refresh billing metadata
            const updatedData = await getBillingMetadata(houseData.id);
            setBillingMetadata(updatedData);
            setEditingField(null);
            setEditValue('');
        } catch (error) {
            setMetadataError(error.message || 'Failed to update billing metadata');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: '25px',
                    width: '600px',
                    maxWidth: '90vw',
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                        sx={{
                            backgroundColor: theme.palette.primary.light,
                            width: 40,
                            height: 40
                        }}
                    >
                        <InfoOutlinedIcon sx={{ color: 'white' }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            View Plan Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Plan rates and house info
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box>
                        <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 2 }} />
                        <Skeleton variant="text" height={40} sx={{ mb: 1 }} />
                        <Skeleton variant="text" height={40} sx={{ mb: 1 }} />
                        <Skeleton variant="text" height={40} />
                    </Box>
                ) : invoiceError && metadataError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Failed to load plan details. Please try again later.
                    </Alert>
                ) : (
                    <Box>
                        {/* Header with Logo and Provider Name */}
                        <Grid container alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Grid item>
                                <Avatar
                                    src={PROVIDER_LOGO_PATH + (houseData?.invoiceprovidername || 'DEI') + '.png'}
                                    alt={houseData?.invoiceprovidername}
                                    sx={{ width: 56, height: 56, bgcolor: theme.palette.grey[100] }}
                                />
                            </Grid>
                            <Grid item xs>
                                <Typography variant="h6" fontWeight={600}>
                                    {houseData?.invoiceprovidername} {houseData?.invoicename}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Electricity Plan
                                </Typography>
                            </Grid>
                        </Grid>

                        <Divider sx={{ mb: 3 }} />

                        {/* Plan Rates Section */}
                        {invoiceData && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                                    Plan Rates
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Daily Rate (High)
                                        </Typography>
                                        <Typography variant="body1" fontWeight={600}>
                                            {invoiceData.day_price_high ? `€${invoiceData.day_price_high.toFixed(5)}/kWh` : '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Daily Rate (Low)
                                        </Typography>
                                        <Typography variant="body1" fontWeight={600}>
                                            {invoiceData.day_price_low ? `€${invoiceData.day_price_low.toFixed(5)}/kWh` : '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Night Rate
                                        </Typography>
                                        <Typography variant="body1" fontWeight={600}>
                                            {invoiceData.night_price ? `€${invoiceData.night_price.toFixed(5)}/kWh` : '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Fixed Monthly Cost
                                        </Typography>
                                        <Typography variant="body1" fontWeight={600}>
                                            {invoiceData.flat_fee ? `€${invoiceData.flat_fee.toFixed(2)}` : '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Billing Period
                                        </Typography>
                                        <Typography variant="body1" fontWeight={600}>
                                            {invoiceData.month || '-'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        <Divider sx={{ my: 3 }} />

                        {/* Additional House Info Section */}
                        {billingMetadata && (
                            <Box>
                                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                                    Additional House Info
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Municipal fees per m²
                                        </Typography>
                                        {editingField === 'dteuroanatm' ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={editValue}
                                                onChange={handleNumericChange}
                                                inputProps={{ min: 0, step: 'any' }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleSaveField}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.success.main }}
                                                            >
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleCancelEdit}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.error.main }}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {billingMetadata.dteuroanatm !== undefined && billingMetadata.dteuroanatm !== null
                                                        ? `€${billingMetadata.dteuroanatm.toFixed(2)}/m²`
                                                        : '-'}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditField('dteuroanatm', billingMetadata.dteuroanatm)}
                                                    sx={{ padding: 0.5 }}
                                                >
                                                    <EditIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Municipal taxes per m²
                                        </Typography>
                                        {editingField === 'dfeuroanatm' ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={editValue}
                                                onChange={handleNumericChange}
                                                inputProps={{ min: 0, step: 'any' }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleSaveField}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.success.main }}
                                                            >
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleCancelEdit}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.error.main }}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {billingMetadata.dfeuroanatm !== undefined && billingMetadata.dfeuroanatm !== null
                                                        ? `€${billingMetadata.dfeuroanatm.toFixed(2)}/m²`
                                                        : '-'}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditField('dfeuroanatm', billingMetadata.dfeuroanatm)}
                                                    sx={{ padding: 0.5 }}
                                                >
                                                    <EditIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Zone Price
                                        </Typography>
                                        {editingField === 'timizonis' ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={editValue}
                                                onChange={handleNumericChange}
                                                inputProps={{ min: 0, step: 'any' }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleSaveField}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.success.main }}
                                                            >
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleCancelEdit}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.error.main }}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {billingMetadata.timizonis !== undefined && billingMetadata.timizonis !== null
                                                        ? `€${billingMetadata.timizonis.toFixed(2)}`
                                                        : '-'}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditField('timizonis', billingMetadata.timizonis)}
                                                    sx={{ padding: 0.5 }}
                                                >
                                                    <EditIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Building Age
                                        </Typography>
                                        {editingField === 'palaiotita' ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={editValue}
                                                onChange={handleNumericChange}
                                                inputProps={{ min: 0, step: 'any' }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleSaveField}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.success.main }}
                                                            >
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleCancelEdit}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.error.main }}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {billingMetadata.palaiotita !== undefined && billingMetadata.palaiotita !== null
                                                        ? billingMetadata.palaiotita.toFixed(2)
                                                        : '-'}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditField('palaiotita', billingMetadata.palaiotita)}
                                                    sx={{ padding: 0.5 }}
                                                >
                                                    <EditIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            TAP Coefficient
                                        </Typography>
                                        {editingField === 'synttap' ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={editValue}
                                                onChange={handleNumericChange}
                                                inputProps={{ min: 0, step: 'any' }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleSaveField}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.success.main }}
                                                            >
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleCancelEdit}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.error.main }}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {billingMetadata.synttap !== undefined && billingMetadata.synttap !== null
                                                        ? billingMetadata.synttap
                                                        : '-'}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditField('synttap', billingMetadata.synttap)}
                                                    sx={{ padding: 0.5 }}
                                                >
                                                    <EditIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Agreed Supply Power
                                        </Typography>
                                        {editingField === 'simfisxyspar' ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={editValue}
                                                onChange={handleNumericChange}
                                                inputProps={{ min: 0, step: 'any' }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleSaveField}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.success.main }}
                                                            >
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleCancelEdit}
                                                                disabled={saving}
                                                                sx={{ color: theme.palette.error.main }}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {billingMetadata.simfisxyspar !== undefined && billingMetadata.simfisxyspar !== null
                                                        ? `${billingMetadata.simfisxyspar} kW`
                                                        : '-'}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditField('simfisxyspar', billingMetadata.simfisxyspar)}
                                                    sx={{ padding: 0.5 }}
                                                >
                                                    <EditIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                            Night Tariff
                                        </Typography>
                                        {editingField === 'nightmeter' ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                    >
                                                        <MenuItem value="false">No</MenuItem>
                                                        <MenuItem value="true">Yes</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleSaveField}
                                                    disabled={saving}
                                                    sx={{ color: theme.palette.success.main }}
                                                >
                                                    <CheckIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleCancelEdit}
                                                    disabled={saving}
                                                    sx={{ color: theme.palette.error.main }}
                                                >
                                                    <CancelIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {billingMetadata.nightmeter !== undefined && billingMetadata.nightmeter !== null
                                                        ? (billingMetadata.nightmeter ? 'Yes' : 'No')
                                                        : '-'}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditField('nightmeter', billingMetadata.nightmeter)}
                                                    sx={{ padding: 0.5 }}
                                                >
                                                    <EditIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ViewPlanDetailsModal;

